import { isNotNil } from '@newfront-insurance/core';
import classnames from 'classnames';
import { useMemo, useState, useEffect, type JSX } from 'react';
import * as React from 'react';
import type { OptionsOrGroups } from 'react-select';
import styled from 'styled-components';

import { IconSelect } from './icons';
import { isGroupedOption } from './lib';
import { fontFamilies } from '../../../theme';
import { colors } from '../../../theme/colors';
import type { Option, GroupedOptions } from '../types';

// TODO(gwintrob): support loading indicator for loading async data
interface Props {
  fieldId?: string;
  defaultValue?: string;
  disabled?: boolean;
  isInvalid?: boolean;
  name?: string;
  showSelectAnOption?: boolean;
  showSelectAnOptionDisabled?: boolean;
  onChange: (value: string) => unknown;
  options: Option[] | GroupedOptions[];
  value?: string | null;
  autoComplete?: 'on' | 'off';
  innerRef?: React.MutableRefObject<HTMLSelectElement | null>;
  placeholder?: string;
  width?: string;
  testId?: string;
  style?: React.CSSProperties;
  iconColor?: string;
  useUpdatedStyles?: boolean;
}

/**
 * Returns an array of <option> and <optgroup> elements
 * @param options Array of options or grouped options
 */
function createOptionElements(
  options: OptionsOrGroups<Option, GroupedOptions>,
  showSelectAnOption?: boolean,
  showSelectAnOptionDisabled?: boolean,
  placeholder?: string,
): JSX.Element[] {
  const els: JSX.Element[] = [];
  if (showSelectAnOption === true) {
    els.push(
      <StyledPlaceholderOption disabled={showSelectAnOptionDisabled} value="" key="select an option">
        {placeholder || '-- select an option --'}
      </StyledPlaceholderOption>,
    );
  }

  options.forEach((option: Option | GroupedOptions) => {
    if (isGroupedOption(option)) {
      els.push(
        <optgroup label={option.label} key={option.label} className="option">
          {createOptionElements(option.options)}
        </optgroup>,
      );
    } else {
      els.push(
        <option value={option.value} key={option.value} className="option">
          {option.label}
        </option>,
      );
    }
  });
  return els;
}

export default function SimpleSelectInput(props: Props): JSX.Element {
  const {
    options,
    fieldId,
    name,
    innerRef,
    iconColor,
    value,
    disabled,
    defaultValue,
    onChange,
    isInvalid,
    showSelectAnOption,
    showSelectAnOptionDisabled = true,
    placeholder,
    style = {},
    testId,
    width,
    useUpdatedStyles,
  } = props;

  const [initialDisabledState, setInitialDisabledState] = useState<boolean | undefined>(
    showSelectAnOption === true && showSelectAnOptionDisabled === true,
  );
  const selectOptions = useMemo(
    () => createOptionElements(options, showSelectAnOption, showSelectAnOptionDisabled, placeholder),
    [options],
  );

  const className = classnames({
    SimpleSelectInput: true,
    isInvalid,
    isInactive: disabled,
    isDisabled: initialDisabledState === true && !disabled,
  });

  function handleChange(e: React.ChangeEvent<HTMLSelectElement>): void {
    if (initialDisabledState) setInitialDisabledState(false);
    onChange(e.target.value);
  }

  useEffect(() => {
    // If the value prop changes outside the onChange event, it should remove the disabled styling
    if (initialDisabledState && value) {
      setInitialDisabledState(false);
    }
  }, [value]);

  const defaultSelectInputAttributes = {
    'data-testid': testId,
    disabled,
    id: fieldId,
    name,
    onChange: handleChange,
    ref: innerRef as React.RefObject<HTMLSelectElement>,
    width,
  };

  // Avoid the following noisy console warning:
  // Select elements must be either controlled or uncontrolled (specify either the value prop,
  // or the defaultValue prop, but not both).
  const selectAttributes = defaultValue
    ? { defaultValue, ...defaultSelectInputAttributes }
    : { value: value || '', ...defaultSelectInputAttributes };

  return (
    <StyledSimpleSelectWrapper className={className}>
      {/* eslint-disable-next-line react/jsx-props-no-spreading */}
      <StyledSimpleSelect
        {...selectAttributes}
        style={style}
        useUpdatedStyles={useUpdatedStyles}
        valueSelected={isNotNil(value)}
      >
        {selectOptions}
      </StyledSimpleSelect>
      <SimpleSelectInputIcon iconColor={iconColor}>
        <IconSelect />
      </SimpleSelectInputIcon>
    </StyledSimpleSelectWrapper>
  );
}

const StyledSimpleSelectWrapper = styled.div`
  position: relative;
  z-index: 1;
`;

const StyledSimpleSelect = styled.select<{ width?: string; useUpdatedStyles?: boolean; valueSelected?: boolean }>`
  appearance: none;
  background: white;
  border: 1px solid ${colors.steel[200]};
  border-radius: 4px;
  box-sizing: border-box;
  color: ${({ valueSelected }) => (valueSelected ? colors.steel[500] : colors.steel[300])};
  display: block;
  font-family: ${fontFamilies.body};
  font-size: 14px;
  height: 40px;
  line-height: 21px;
  padding: 0 32px 0 12px;
  width: ${({ width }) => width || '100%'};

  &:focus,
  &:active:not(:disabled) {
    border-color: ${colors.brand[400]};
    outline: none;
  }

  .isInactive & {
    color: ${colors.steel[300]};
    background: #f5f6f8;
  }

  .isDisabled & {
    color: ${colors.steel[300]};
  }

  &.isInvalid {
    border-color: ${colors.fire[600]};
    box-shadow: 0 0 0 3px rgba(189, 73, 50, 0.25);
  }
`;

const StyledPlaceholderOption = styled.option`
  color: #000;

  &:disabled {
    color: ${colors.steel[200]};
  }
`;

const SimpleSelectInputIcon = styled.div<{ iconColor?: string }>`
  align-items: center;
  color: ${({ iconColor }) => iconColor || colors.steel[150]};
  display: flex;
  height: 20px;
  justify-content: center;
  pointer-events: none;
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  width: 20px;

  &.SimpleSelectInput-control:focus {
    color: ${colors.brand[400]};
  }
`;
